import { useMemo } from "react";

/**
 * A slow, subtle, continuously-scrolling strip.
 * Built with CSS keyframes — no JS rAF, no jank.
 *
 * Props:
 *  - items: array of { src, label }
 *  - labels: array of "section labels" rendered as text dividers
 *  - speedSeconds: full loop duration (slower = larger number)
 */
export const Marquee = ({ items = [], labels = [], speedSeconds = 90 }) => {
    // Flatten labels + items into a single ribbon then duplicate for seamless loop.
    const ribbon = useMemo(() => {
        const arr = [];
        const usedLabels = new Set();
        items.forEach((it, idx) => {
            arr.push({ kind: "img", ...it, key: `img-${idx}` });
            if (labels.length && idx % 2 === 1) {
                const text =
                    labels[Math.floor(idx / 2) % labels.length] || labels[0];
                usedLabels.add(text);
                arr.push({
                    kind: "label",
                    text,
                    key: `lbl-${idx}`,
                });
            }
        });
        // Safety net: ensure every label appears at least once (handles small item counts).
        labels.forEach((text, i) => {
            if (!usedLabels.has(text)) {
                arr.push({ kind: "label", text, key: `lbl-extra-${i}` });
            }
        });
        return arr;
    }, [items, labels]);

    if (ribbon.length === 0) return null;

    return (
        <div
            data-testid="home-marquee"
            className="relative border-y border-border overflow-hidden bg-background/40"
            style={{ ["--marquee-duration"]: `${speedSeconds}s` }}
        >
            <div
                className="absolute inset-y-0 left-0 w-32 z-10 pointer-events-none"
                style={{
                    background:
                        "linear-gradient(to right, hsl(var(--background)), transparent)",
                }}
            />
            <div
                className="absolute inset-y-0 right-0 w-32 z-10 pointer-events-none"
                style={{
                    background:
                        "linear-gradient(to left, hsl(var(--background)), transparent)",
                }}
            />

            <div className="marquee-track flex items-center gap-10 py-6">
                {[0, 1].map((dup) => (
                    <div
                        key={dup}
                        aria-hidden={dup === 1}
                        className="flex items-center gap-10 shrink-0"
                    >
                        {ribbon.map((node) =>
                            node.kind === "img" ? (
                                <div
                                    key={`${dup}-${node.key}`}
                                    className="relative h-20 sm:h-24 w-32 sm:w-40 bg-muted overflow-hidden shrink-0"
                                >
                                    <img
                                        src={node.src}
                                        alt={node.label || ""}
                                        loading="lazy"
                                        className="w-full h-full object-cover opacity-90"
                                    />
                                </div>
                            ) : (
                                <span
                                    key={`${dup}-${node.key}`}
                                    className="text-label text-muted-foreground whitespace-nowrap shrink-0 px-2"
                                >
                                    {node.text}
                                </span>
                            ),
                        )}
                    </div>
                ))}
            </div>

            <style>{`
                .marquee-track { animation: marquee-scroll var(--marquee-duration) linear infinite; will-change: transform; }
                @keyframes marquee-scroll {
                    from { transform: translateX(0); }
                    to { transform: translateX(-50%); }
                }
                @media (prefers-reduced-motion: reduce) {
                    .marquee-track { animation: none; transform: none; }
                }
            `}</style>
        </div>
    );
};

export default Marquee;
